import AccountSync from "../services/AccountSync.js";
import ScoreUtils from "../utils/ScoreUtils.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import * as Passport from "../utils/Passport.js";
import * as ReviewDeck from "../utils/ReviewDeck.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { buildChallenge, needsBorders, needsShapes } from "../utils/ChallengeQuestions.js";
import { loadCountryDetails } from "../utils/CountryDetails.js";
import { readWorldPaths } from "../utils/SvgShapes.js";
import { renderShape } from "./ShapeNerd.js";
import { bindAnswerKeys } from "../utils/QuizKeyboard.js";
import { formatDuration } from "../utils/TimeUtils.js";
import { recordLabPlay, renderLabRating } from "../utils/LabFeedback.js";

/** Thèmes proposés. « mix » (drapeaux et capitales) n'est plus proposé mais reste lu dans les anciens liens. */
export const THEMES = ["flags", "capitals", "borders", "shapes", "all"];
const KNOWN_THEMES = [...THEMES, "mix"];
/** 10 questions : 3 faciles, 3 moyennes, 4 difficiles (mêmes paliers que le Défi du jour). */
export const TIERS = [
	{ maxRank: 50, count: 3 },
	{ maxRank: 120, count: 3 },
	{ maxRank: Infinity, count: 4 },
];
const NAME_KEY = "geonerd.challengeName";

const escapeHtml = (s) => String(s)
	.replace(/&/g, "&amp;")
	.replace(/</g, "&lt;")
	.replace(/>/g, "&gt;")
	.replace(/"/g, "&quot;");

/**
 * Lit un défi reçu dans l'URL : graine, thème, et le résultat du lanceur.
 * @returns {null|{seed: number, theme: string, score: number|null, duration: number|null, name: string}}
 */
export function parseChallenge(search = window.location.search) {
	const params = new URLSearchParams(search);
	const seed = parseInt(params.get("s"), 10);
	if (!Number.isInteger(seed) || seed <= 0) return null;
	const theme = KNOWN_THEMES.includes(params.get("t")) ? params.get("t") : "flags";
	const score = parseInt(params.get("sc"), 10);
	const duration = parseInt(params.get("d"), 10);
	return {
		seed,
		theme,
		score: Number.isInteger(score) && score >= 0 && score <= 10 ? score : null,
		duration: Number.isInteger(duration) && duration > 0 ? duration : null,
		name: (params.get("n") || "").slice(0, 20).trim(),
	};
}

export function challengeUrl({ seed, theme, score, duration, name }, lang) {
	const prefix = lang === "en" ? "/en" : "";
	const params = new URLSearchParams({ s: String(seed), t: theme, sc: String(score), d: String(duration) });
	if (name) params.set("n", name.slice(0, 20));
	return `https://geo-nerd.com${prefix}/friend-challenge?${params}`;
}

/** Qui gagne : plus de bonnes réponses, puis le plus rapide. */
export function compare(mine, theirs) {
	if (theirs.score === null) return "none";
	if (mine.score !== theirs.score) return mine.score > theirs.score ? "win" : "lose";
	if (theirs.duration && mine.duration !== theirs.duration) return mine.duration < theirs.duration ? "win" : "lose";
	return "tie";
}

/**
 * Silhouettes pour les thèmes qui en contiennent. Le SVG du monde (845 Ko de
 * page) n'est pas inclus dans celle du défi, page d'arrivée des liens partagés :
 * on le lit dans la page de Shape Nerd, seulement quand il sert (souvent déjà
 * en cache). Pays retenus : ceux de la carte jouable, comme dans Shape Nerd.
 */
let shapesPromise = null;
function loadChallengeShapes() {
	if (!shapesPromise) {
		shapesPromise = fetch("/shape-nerd")
			.then(r => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.text();
			})
			.then(html => {
				const paths = readWorldPaths(new DOMParser().parseFromString(html, "text/html"));
				const codes = new Set(window.geoNerdApp.easyCountries.filter(c => paths[c.code]).map(c => c.code));
				return { paths, boxes: {}, codes };
			})
			.catch(e => {
				shapesPromise = null;
				throw e;
			});
	}
	return shapesPromise;
}

/**
 * Défi un ami (52 du TODO, Lab) : 10 questions tirées d'une graine portée par
 * le lien. Le lanceur joue, partage le lien avec son score ; l'ami joue
 * exactement les mêmes questions et voit qui l'emporte, puis peut renvoyer le
 * défi avec son propre score. Aucun compte, rien côté serveur : le bouche à
 * oreille qui amène déjà les joueurs, avec un prétexte de plus.
 */
export default class FriendChallenge {
	constructor() {
		this.page = document.querySelector(".page.friend-challenge");
		if (!this.page) return;
		this.lang = window.geoNerdApp.languageManager;
		this.t = (k) => this.lang.t(k);
		this.body = this.page.querySelector(".challenge-body");
		this.received = parseChallenge();
		this.renderIntro();
	}

	renderIntro() {
		if (this.received) {
			const who = escapeHtml(this.received.name || this.t("lab.challenge.someone"));
			const result = this.received.score !== null
				? this.t("lab.challenge.theirResult")
					.replace("{score}", this.received.score)
					.replace("{time}", this.received.duration ? formatDuration(this.received.duration) : "?")
				: "";
			this.body.innerHTML = `
				<p class="challenge-invite">${this.t("lab.challenge.invite").replace("{name}", who)}</p>
				${result ? `<p class="challenge-their">${result}</p>` : ""}
				<p class="challenge-theme">${this.t("lab.challenge.themeLine").replace("{theme}", this.t(`lab.challenge.themeName.${this.received.theme}`))}</p>
				<button type="button" class="btn challenge-go">${this.t("lab.challenge.accept")}</button>`;
			this.body.querySelector(".challenge-go").addEventListener("click", () => this.play(this.received.seed, this.received.theme));
			return;
		}
		this.body.innerHTML = `
			<p class="challenge-pitch">${this.t("lab.challenge.pitch")}</p>
			<div class="challenge-themes">
				${THEMES.map(theme => `<button type="button" class="btn challenge-theme-btn" data-theme="${theme}">${this.t(`lab.challenge.theme.${theme}`)}</button>`).join("")}
			</div>`;
		this.body.querySelectorAll(".challenge-theme-btn").forEach(btn => btn.addEventListener("click", () => {
			this.play(1 + Math.floor(Math.random() * 2_000_000_000), btn.dataset.theme);
		}));
	}

	async play(seed, theme) {
		this.seed = seed;
		this.theme = theme;
		this.body.innerHTML = `<p class="challenge-loading">${this.t("lab.challenge.loading")}</p>`;
		let details = {};
		try {
			[details, this.shapes] = await Promise.all([
				needsBorders(theme) ? loadCountryDetails(this.lang.currentLang) : {},
				needsShapes(theme) ? loadChallengeShapes() : null,
			]);
		} catch (e) {
			this.body.innerHTML = `
				<p class="challenge-loading">${this.t("lab.challenge.loadError")}</p>
				<button type="button" class="btn challenge-retry">${this.t("lab.challenge.retry")}</button>`;
			this.body.querySelector(".challenge-retry").addEventListener("click", () => this.play(seed, theme));
			return;
		}
		const borders = Object.fromEntries(Object.entries(details).map(([code, d]) => [code, d.borders || []]));
		this.questions = buildChallenge({
			countries: window.geoNerdApp.countries,
			seed,
			theme,
			tiers: TIERS,
			borders,
			shapeCodes: this.shapes?.codes,
		});
		this.index = 0;
		this.score = 0;
		this.results = [];
		this.startedAt = Date.now();
		this.body.innerHTML = `
			<div class="progress"><div class="progress-steps">${this.questions.map(() => `<span class="step"></span>`).join("")}</div></div>
			<div class="flag-container"></div>
			<div class="answer-container"></div>`;
		this.flagContainer = this.body.querySelector(".flag-container");
		this.answers = this.body.querySelector(".answer-container");
		this.unbindKeys = bindAnswerKeys(this.answers);
		this.next();
	}

	next() {
		if (this.index >= this.questions.length) {
			this.finish();
			return;
		}
		const q = this.questions[this.index];
		this.flagContainer.innerHTML = this.renderQuestion(q);
		this.answered = false;
		this.answers.innerHTML = q.proposals.map(p =>
			`<button type="button" class="country" data-code="${p.code}">${escapeHtml(q.type === "capital" ? p.capital : p.name)}</button>`
		).join("");
		this.answers.querySelectorAll(".country").forEach(btn => btn.addEventListener("click", () => this.answer(q, btn)));
	}

	/** Même ordre que les autres modes du Lab : la question, puis ce qu'elle désigne. */
	renderQuestion(q) {
		const flag = `<div class="flag" style="background-image: url(${getFlagPath(q.country.code)})"></div>`;
		const prompt = (key) => `<p class="lab-prompt">${this.t(key)}</p>`;
		const named = `<div class="daily-capital">${flag}<p class="daily-capital-country">${escapeHtml(q.country.name)}</p></div>`;
		if (q.type === "capital") return prompt("daily.capitalQuestion") + named;
		if (q.type === "border") return prompt("lab.challenge.borderQuestion") + named;
		if (q.type === "shape") return prompt("lab.shapeQuestion") + renderShape(q.country.code, this.shapes.paths, this.shapes.boxes);
		return prompt("game.whatIsThisFlag") + flag;
	}

	answer(q, btn) {
		if (this.answered) return;
		this.answered = true;
		const correct = btn.dataset.code === q.answer;
		// Passeport et révision ne suivent que les drapeaux et les capitales.
		const skill = q.type === "capital" ? "capital" : q.type === "flag" ? "flag" : null;
		this.results.push(correct);
		this.body.querySelectorAll(".progress-steps .step")[this.index]?.classList.add(correct ? "correct" : "wrong");
		if (correct) {
			this.score++;
			if (skill) Passport.stamp(q.country.code, skill);
			SoundManager.correct();
			FeedbackEffects.confetti(btn);
			btn.classList.add("valid");
		} else {
			if (skill) ReviewDeck.recordMiss(skill, q.country.code);
			SoundManager.wrong();
			btn.classList.add("invalid");
			this.answers.querySelector(`[data-code="${q.answer}"]`)?.classList.add("valid");
		}
		setTimeout(() => {
			this.index++;
			this.next();
		}, correct ? 700 : 1300);
	}

	finish() {
		if (this.unbindKeys) this.unbindKeys();
		this.duration = Math.max(1, Math.round((Date.now() - this.startedAt) / 1000));
		ScoreUtils.saveStats("friendchallenge", this.score);
		AccountSync.push();
		recordLabPlay("friendchallenge");
		SoundManager.victory();

		const mine = { score: this.score, duration: this.duration };
		const outcome = this.received && this.received.seed === this.seed ? compare(mine, this.received) : "none";
		const who = escapeHtml(this.received?.name || this.t("lab.challenge.someone"));
		const verdict = outcome === "none" ? "" : `<p class="challenge-verdict is-${outcome}">${this.t(`lab.challenge.${outcome}`).replace("{name}", who)}</p>`;
		const grid = this.results.map(r => (r ? "🟩" : "🟥")).join("");
		const name = AccountSync.user?.username || localStorage.getItem(NAME_KEY) || "";

		this.body.innerHTML = `
			<p class="challenge-score"><strong>${this.score}/10</strong> · ${formatDuration(this.duration)}</p>
			<p class="challenge-grid">${grid}</p>
			${verdict}
			<label class="challenge-name">
				<span>${this.t("lab.challenge.yourName")}</span>
				<input type="text" maxlength="20" value="${escapeHtml(name)}" autocomplete="nickname">
			</label>
			<div class="message-actions">
				<button type="button" class="btn btn-share challenge-share"><span class="share-icon"></span><span class="share-text">${this.t(outcome === "none" ? "lab.challenge.send" : "lab.challenge.sendBack")}</span></button>
				<button type="button" class="btn btn-secondary challenge-new">${this.t("lab.challenge.new")}</button>
			</div>`;
		renderLabRating(this.body, "friendchallenge", this.body.querySelector(".message-actions"));
		this.body.querySelector(".challenge-share").addEventListener("click", (e) => this.share(e.currentTarget, grid));
		this.body.querySelector(".challenge-new").addEventListener("click", () => {
			const prefix = this.lang.currentLang === "en" ? "/en" : "";
			window.location.href = `${prefix}/friend-challenge`;
		});
	}

	async share(btn, grid) {
		const name = this.body.querySelector(".challenge-name input").value.replace(/[<>]/g, "").trim().slice(0, 20);
		if (name) localStorage.setItem(NAME_KEY, name);
		const url = challengeUrl({ seed: this.seed, theme: this.theme, score: this.score, duration: this.duration, name }, this.lang.currentLang);
		const text = `${this.t("lab.challenge.shareText").replace("{score}", this.score)}\n${grid}\n${url}`;
		const label = btn.querySelector(".share-text");
		const original = label.textContent;
		try {
			if (navigator.share) await navigator.share({ text });
			else await navigator.clipboard.writeText(text);
			if (!navigator.share) label.textContent = this.t("scores.copied");
		} catch (e) {
			// Partage annulé : rien à signaler.
		}
		setTimeout(() => { label.textContent = original; }, 2000);
	}
}
